import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Badge, Button, ProgressBar, Alert, Modal, Nav, Tab } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRobot,
  faCheckCircle,
  faClock,
  faPlay,
  faPause,
  faTimes,
  faExclamationTriangle,
  faRefresh,
  faInfoCircle,
  faBolt,
  faEnvelope,
  faPhone,
  faChartLine,
  faUsers,
  faCalendar,
  faCheck,
  faSpinner,
  faEye
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from 'helpers/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

interface WorkflowTask {
  task_id: string;
  lead_id: string;
  agent_type: 'response' | 'intelligence' | 'nurture' | 'monitor';
  action: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  scheduled_for: string;
  status: 'pending' | 'executing' | 'completed' | 'failed' | 'escalated';
  requires_approval: boolean;
  auto_executable: boolean;
  task_data?: any;
  execution_log?: any;
  error_message?: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
}

interface WorkflowInstance {
  instance_id: string;
  lead_id: string;
  workflow_template_id: string;
  current_stage_id: number;
  status: 'ACTIVE' | 'COMPLETED' | 'PAUSED';
  automation_level: 'manual' | 'supervised' | 'autonomous';
  created_at: string;
  completed_at?: string;
}

interface LeadDetailsAutomationProps {
  leadId: string;
  leadDetails?: any;
  refreshTrigger?: number;
}

const LeadDetailsAutomation: React.FC<LeadDetailsAutomationProps> = ({ 
  leadId, 
  leadDetails,
  refreshTrigger 
}) => {
  const [workflow, setWorkflow] = useState<WorkflowInstance | null>(null);
  const [tasks, setTasks] = useState<WorkflowTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [processingTask, setProcessingTask] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<WorkflowTask | null>(null);
  const [showTaskModal, setShowTaskModal] = useState(false);

  useEffect(() => {
    if (leadId) {
      fetchWorkflowData();
    }
  }, [leadId, refreshTrigger]);

  const fetchWorkflowData = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/agent-kairos/leads/${leadId}/workflow`);
      
      console.log('[DEBUG] Workflow API response:', response.data);
      
      if (response.data.success && response.data.data) {
        setWorkflow(response.data.data.workflow);
        setTasks(response.data.data.tasks || []);
      } else if (response.data.success && !response.data.data) {
        // No workflow found for this lead
        console.log('[DEBUG] No workflow found for lead:', leadId);
        setWorkflow(null);
        setTasks([]);
      }
    } catch (error) {
      console.error('Error fetching workflow data:', error);
      if (error.response) {
        console.error('[DEBUG] Error response:', error.response.data);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApproveTask = async (taskId: string) => {
    try {
      setProcessingTask(taskId);
      await axiosInstance.post(`/agent-kairos/tasks/${taskId}/approve`);
      toast.success('Task approved for execution');
      fetchWorkflowData();
    } catch (error) {
      toast.error('Failed to approve task');
    } finally {
      setProcessingTask(null);
    }
  };

  const handleApproveAll = async () => {
    const pendingTasks = tasks.filter(t => t.status === 'pending' && t.requires_approval);
    
    if (pendingTasks.length === 0) {
      toast('No tasks require approval', { icon: 'ℹ️' });
      return;
    }

    try {
      setProcessingTask('all');
      await Promise.all(
        pendingTasks.map(task => 
          axiosInstance.post(`/agent-kairos/tasks/${task.task_id}/approve`)
        )
      );
      toast.success(`${pendingTasks.length} tasks approved`);
      fetchWorkflowData();
    } catch (error) {
      toast.error('Failed to approve all tasks');
    } finally {
      setProcessingTask(null);
    }
  };

  const handlePauseResume = async () => {
    if (!workflow) return;

    try {
      const newStatus = workflow.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
      // API endpoint would be needed here
      toast.success(`Workflow ${newStatus.toLowerCase()}`);
      fetchWorkflowData();
    } catch (error) {
      toast.error('Failed to update workflow status');
    }
  };

  const getAgentIcon = (agentType: string) => {
    switch (agentType) {
      case 'response': return faEnvelope;
      case 'intelligence': return faChartLine;
      case 'nurture': return faUsers;
      case 'monitor': return faChartLine;
      default: return faRobot;
    }
  };

  const getActionLabel = (action: string) => {
    const labels: { [key: string]: string } = {
      'send_reengagement_email': 'Re-engagement Email',
      'analyze_property_matches': 'Property Analysis',
      'send_property_matches': 'Property Matches Email',
      'schedule_consultation': 'Schedule Consultation',
      'send_market_update': 'Market Update Email',
      'follow_up_call': 'Follow-up Call',
      'send_newsletter': 'Newsletter',
      'update_lead_score': 'Update Lead Score'
    };
    return labels[action] || action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'danger';
      case 'high': return 'warning';
      case 'medium': return 'primary';
      case 'low': return 'secondary';
      default: return 'light';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'executing': return 'info';
      case 'pending': return 'warning';
      case 'failed': return 'danger';
      case 'escalated': return 'dark';
      default: return 'secondary';
    }
  };

  // Categorize tasks
  const pendingTasks = tasks.filter(t => t.status === 'pending');
  const completedTasks = tasks.filter(t => t.status === 'completed');
  const failedTasks = tasks.filter(t => t.status === 'failed' || t.status === 'escalated');
  const executingTasks = tasks.filter(t => t.status === 'executing');

  const pendingApproval = pendingTasks.filter(t => t.requires_approval);
  const autoExecutable = pendingTasks.filter(t => !t.requires_approval);

  const completionRate = tasks.length > 0 
    ? Math.round((completedTasks.length / tasks.length) * 100) 
    : 0;

  if (loading) {
    return (
      <div className="text-center py-5">
        <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-primary mb-3" />
        <div>Loading automation data...</div>
      </div>
    );
  }

  if (!workflow && tasks.length === 0) {
    return (
      <Alert variant="info" className="d-flex align-items-center">
        <FontAwesomeIcon icon={faRobot} className="me-2" />
        <div>
          <strong>No Active Automation</strong>
          <div className="small">This lead doesn't have an active Agent Kairos workflow.</div>
        </div>
        <Button variant="primary" size="sm" className="ms-auto">
          Start Workflow
        </Button>
      </Alert>
    );
  }

  return (
    <div className="lead-automation-container">
      {/* Workflow Status Overview */}
      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">
              <FontAwesomeIcon icon={faBolt} className="me-2 text-primary" />
              Agent Kairos Automation
            </h5>
            <div className="d-flex gap-2">
              <Button 
                size="sm" 
                variant="outline-secondary"
                onClick={fetchWorkflowData}
              >
                <FontAwesomeIcon icon={faRefresh} />
              </Button>
              {workflow && (
                <Button 
                  size="sm" 
                  variant={workflow.status === 'ACTIVE' ? 'warning' : 'success'}
                  onClick={handlePauseResume}
                >
                  <FontAwesomeIcon icon={workflow.status === 'ACTIVE' ? faPause : faPlay} />
                  <span className="ms-1 d-none d-sm-inline">
                    {workflow.status === 'ACTIVE' ? 'Pause' : 'Resume'}
                  </span>
                </Button>
              )}
            </div>
          </div>

          {/* Progress Overview */}
          <Row className="g-3 mb-3">
            <Col xs={6} sm={3}>
              <div className="text-center">
                <div className="fs-2 fw-bold text-primary">{completionRate}%</div>
                <small className="text-muted">Complete</small>
              </div>
            </Col>
            <Col xs={6} sm={3}>
              <div className="text-center">
                <div className="fs-2 fw-bold text-warning">{pendingApproval.length}</div>
                <small className="text-muted">Need Approval</small>
              </div>
            </Col>
            <Col xs={6} sm={3}>
              <div className="text-center">
                <div className="fs-2 fw-bold text-success">{completedTasks.length}</div>
                <small className="text-muted">Completed</small>
              </div>
            </Col>
            <Col xs={6} sm={3}>
              <div className="text-center">
                <div className="fs-2 fw-bold text-info">{autoExecutable.length}</div>
                <small className="text-muted">Scheduled</small>
              </div>
            </Col>
          </Row>

          <ProgressBar>
            <ProgressBar 
              now={(completedTasks.length / tasks.length) * 100} 
              variant="success" 
              label={`${completedTasks.length}`}
            />
            <ProgressBar 
              now={(executingTasks.length / tasks.length) * 100} 
              variant="info" 
              label={executingTasks.length > 0 ? `${executingTasks.length}` : ''}
            />
            <ProgressBar 
              now={(pendingTasks.length / tasks.length) * 100} 
              variant="warning" 
              label={`${pendingTasks.length}`}
            />
            <ProgressBar 
              now={(failedTasks.length / tasks.length) * 100} 
              variant="danger" 
              label={failedTasks.length > 0 ? `${failedTasks.length}` : ''}
            />
          </ProgressBar>
          <div className="d-flex justify-content-between mt-1">
            <small className="text-muted">
              Started {workflow ? format(new Date(workflow.created_at), 'MMM dd, yyyy') : 'N/A'}
            </small>
            <small className="text-muted">
              {workflow?.automation_level || 'Supervised'} Mode
            </small>
          </div>
        </Card.Body>
      </Card>

      {/* Approval Actions */}
      {pendingApproval.length > 0 && (
        <Alert variant="warning" className="d-flex align-items-center mb-3">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          <div className="flex-grow-1">
            <strong>{pendingApproval.length} tasks</strong> require your approval
          </div>
          <Button 
            size="sm" 
            variant="success"
            onClick={handleApproveAll}
            disabled={processingTask === 'all'}
          >
            {processingTask === 'all' ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <>
                <FontAwesomeIcon icon={faCheck} className="me-1" />
                Approve All
              </>
            )}
          </Button>
        </Alert>
      )}

      {/* Task Tabs */}
      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'pending')}>
        <Nav variant="tabs" className="mb-3">
          <Nav.Item>
            <Nav.Link eventKey="pending">
              Pending ({pendingTasks.length})
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="completed">
              Completed ({completedTasks.length})
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="timeline">
              Timeline
            </Nav.Link>
          </Nav.Item>
          {failedTasks.length > 0 && (
            <Nav.Item>
              <Nav.Link eventKey="failed" className="text-danger">
                Failed ({failedTasks.length})
              </Nav.Link>
            </Nav.Item>
          )}
        </Nav>

        <Tab.Content>
          {/* Pending Tasks Tab */}
          <Tab.Pane eventKey="pending">
            <div className="task-list">
              {pendingApproval.length > 0 && (
                <>
                  <h6 className="text-warning mb-3">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    Requires Approval
                  </h6>
                  {pendingApproval.map(task => (
                    <Card key={task.task_id} className="mb-2 border-warning">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center mb-2">
                              <FontAwesomeIcon 
                                icon={getAgentIcon(task.agent_type)} 
                                className="me-2 text-primary"
                              />
                              <strong>{getActionLabel(task.action)}</strong>
                              <Badge 
                                bg={getPriorityColor(task.priority)} 
                                className="ms-2"
                              >
                                {task.priority}
                              </Badge>
                            </div>
                            <div className="small text-muted">
                              <FontAwesomeIcon icon={faCalendar} className="me-1" />
                              Scheduled: {format(new Date(task.scheduled_for), 'MMM dd, h:mm a')}
                            </div>
                            {task.task_data?.message && (
                              <div className="mt-2 small">
                                <em className="text-truncate d-block" style={{ maxWidth: '400px' }}>
                                  "{task.task_data.message}"
                                </em>
                              </div>
                            )}
                          </div>
                          <div className="d-flex gap-2">
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => {
                                setSelectedTask(task);
                                setShowTaskModal(true);
                              }}
                            >
                              <FontAwesomeIcon icon={faEye} />
                            </Button>
                            <Button
                              size="sm"
                              variant="success"
                              onClick={() => handleApproveTask(task.task_id)}
                              disabled={processingTask === task.task_id}
                            >
                              {processingTask === task.task_id ? (
                                <FontAwesomeIcon icon={faSpinner} spin />
                              ) : (
                                <>
                                  <FontAwesomeIcon icon={faCheck} />
                                  <span className="ms-1 d-none d-sm-inline">Approve</span>
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </>
              )}

              {autoExecutable.length > 0 && (
                <>
                  <h6 className="text-info mb-3 mt-4">
                    <FontAwesomeIcon icon={faClock} className="me-2" />
                    Scheduled (Auto-Execute)
                  </h6>
                  {autoExecutable.map(task => (
                    <Card key={task.task_id} className="mb-2">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center mb-2">
                              <FontAwesomeIcon 
                                icon={getAgentIcon(task.agent_type)} 
                                className="me-2 text-primary"
                              />
                              <strong>{getActionLabel(task.action)}</strong>
                              <Badge 
                                bg={getPriorityColor(task.priority)} 
                                className="ms-2"
                              >
                                {task.priority}
                              </Badge>
                              <Badge bg="info" className="ms-2">Auto</Badge>
                            </div>
                            <div className="small text-muted">
                              <FontAwesomeIcon icon={faCalendar} className="me-1" />
                              Scheduled: {format(new Date(task.scheduled_for), 'MMM dd, h:mm a')}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => {
                              setSelectedTask(task);
                              setShowTaskModal(true);
                            }}
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </>
              )}

              {pendingTasks.length === 0 && (
                <Alert variant="info">
                  <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                  No pending tasks in the automation queue.
                </Alert>
              )}
            </div>
          </Tab.Pane>

          {/* Completed Tasks Tab */}
          <Tab.Pane eventKey="completed">
            <div className="task-list">
              {completedTasks.map(task => (
                <Card key={task.task_id} className="mb-2 border-success">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center mb-2">
                          <FontAwesomeIcon 
                            icon={faCheckCircle} 
                            className="me-2 text-success"
                          />
                          <strong>{getActionLabel(task.action)}</strong>
                        </div>
                        <div className="small text-muted">
                          {task.completed_at && (
                            <>
                              <FontAwesomeIcon icon={faCalendar} className="me-1" />
                              Completed: {format(new Date(task.completed_at), 'MMM dd, h:mm a')}
                            </>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => {
                          setSelectedTask(task);
                          setShowTaskModal(true);
                        }}
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              ))}

              {completedTasks.length === 0 && (
                <Alert variant="info">
                  <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                  No completed tasks yet.
                </Alert>
              )}
            </div>
          </Tab.Pane>

          {/* Timeline Tab */}
          <Tab.Pane eventKey="timeline">
            <div className="automation-timeline">
              {tasks
                .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                .map((task, index) => (
                  <div key={task.task_id} className="timeline-item d-flex mb-3">
                    <div className="timeline-marker">
                      <FontAwesomeIcon 
                        icon={getAgentIcon(task.agent_type)} 
                        className={`text-${getStatusColor(task.status)}`}
                        size="lg"
                      />
                      {index < tasks.length - 1 && (
                        <div className="timeline-line"></div>
                      )}
                    </div>
                    <div className="timeline-content flex-grow-1 ms-3">
                      <Card>
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <h6 className="mb-1">{getActionLabel(task.action)}</h6>
                              <div className="d-flex gap-2 mb-2">
                                <Badge bg={getStatusColor(task.status)}>
                                  {task.status}
                                </Badge>
                                <Badge bg={getPriorityColor(task.priority)}>
                                  {task.priority}
                                </Badge>
                                {task.requires_approval && (
                                  <Badge bg="warning">Approval Required</Badge>
                                )}
                              </div>
                              <small className="text-muted">
                                Created: {format(new Date(task.created_at), 'MMM dd, h:mm a')}
                              </small>
                            </div>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => {
                                setSelectedTask(task);
                                setShowTaskModal(true);
                              }}
                            >
                              Details
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    </div>
                  </div>
              ))}
            </div>
          </Tab.Pane>

          {/* Failed Tasks Tab */}
          {failedTasks.length > 0 && (
            <Tab.Pane eventKey="failed">
              <div className="task-list">
                {failedTasks.map(task => (
                  <Card key={task.task_id} className="mb-2 border-danger">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center mb-2">
                            <FontAwesomeIcon 
                              icon={faTimes} 
                              className="me-2 text-danger"
                            />
                            <strong>{getActionLabel(task.action)}</strong>
                            <Badge bg="danger" className="ms-2">
                              {task.status}
                            </Badge>
                          </div>
                          {task.error_message && (
                            <Alert variant="danger" className="small mb-0">
                              {task.error_message}
                            </Alert>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => {
                            setSelectedTask(task);
                            setShowTaskModal(true);
                          }}
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            </Tab.Pane>
          )}
        </Tab.Content>
      </Tab.Container>

      {/* Task Detail Modal */}
      <Modal size="lg" show={showTaskModal} onHide={() => setShowTaskModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Task Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTask && (
            <div>
              <Row className="mb-3">
                <Col sm={6}>
                  <strong>Action:</strong> {getActionLabel(selectedTask.action)}
                </Col>
                <Col sm={6}>
                  <strong>Agent:</strong> {selectedTask.agent_type}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col sm={6}>
                  <strong>Status:</strong>{' '}
                  <Badge bg={getStatusColor(selectedTask.status)}>
                    {selectedTask.status}
                  </Badge>
                </Col>
                <Col sm={6}>
                  <strong>Priority:</strong>{' '}
                  <Badge bg={getPriorityColor(selectedTask.priority)}>
                    {selectedTask.priority}
                  </Badge>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col sm={6}>
                  <strong>Created:</strong> {format(new Date(selectedTask.created_at), 'MMM dd, yyyy h:mm a')}
                </Col>
                <Col sm={6}>
                  <strong>Scheduled:</strong> {format(new Date(selectedTask.scheduled_for), 'MMM dd, yyyy h:mm a')}
                </Col>
              </Row>
              {selectedTask.completed_at && (
                <Row className="mb-3">
                  <Col>
                    <strong>Completed:</strong> {format(new Date(selectedTask.completed_at), 'MMM dd, yyyy h:mm a')}
                  </Col>
                </Row>
              )}
              
              {selectedTask.task_data && (
                <div className="mb-3">
                  <strong>Task Data:</strong>
                  <pre className="bg-light p-2 mt-2 rounded">
                    {JSON.stringify(selectedTask.task_data, null, 2)}
                  </pre>
                </div>
              )}

              {selectedTask.execution_log && (
                <div className="mb-3">
                  <strong>Execution Log:</strong>
                  <pre className="bg-light p-3 mt-2 rounded" style={{ maxHeight: '300px', overflow: 'auto' }}>
                    {typeof selectedTask.execution_log === 'string' 
                      ? selectedTask.execution_log 
                      : JSON.stringify(selectedTask.execution_log, null, 2)}
                  </pre>
                </div>
              )}

              {selectedTask.error_message && (
                <Alert variant="danger">
                  <strong>Error:</strong> {selectedTask.error_message}
                </Alert>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTaskModal(false)}>
            Close
          </Button>
          {selectedTask?.status === 'pending' && selectedTask.requires_approval && (
            <Button 
              variant="success"
              onClick={() => {
                handleApproveTask(selectedTask.task_id);
                setShowTaskModal(false);
              }}
              disabled={processingTask === selectedTask.task_id}
            >
              {processingTask === selectedTask.task_id ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheck} className="me-1" />
                  Approve Task
                </>
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <style dangerouslySetInnerHTML={{
        __html: `
          .automation-timeline {
            position: relative;
            padding-left: 30px;
          }

          .timeline-marker {
            position: relative;
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 40px;
          }

          .timeline-line {
            position: absolute;
            top: 30px;
            left: 19px;
            width: 2px;
            height: calc(100% + 12px);
            background: #dee2e6;
          }

          .timeline-content {
            padding-bottom: 1rem;
          }

          .task-list {
            max-height: 500px;
            overflow-y: auto;
            padding-right: 10px;
          }

          .task-list::-webkit-scrollbar {
            width: 8px;
          }

          .task-list::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 4px;
          }

          .task-list::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 4px;
          }

          .task-list::-webkit-scrollbar-thumb:hover {
            background: #555;
          }
        `
      }} />
    </div>
  );
};

export default LeadDetailsAutomation;